import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Package, CheckCircle2, AlertCircle, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";

/**
 * Phase 98al — Auto-mapping produits AgroManage ↔ NayaxProductID.
 *
 * Flow :
 *  1. Loading : POST /nayax/auto-map-products (lit machineProducts de tous les
 *     distributeurs configurés, propose des matches par nom).
 *  2. L'utilisateur valide les propositions checkbox par ligne, avec recherche.
 *  3. Clic "Appliquer" → POST /nayax/apply-product-mapping → toast + onApplied.
 */
export default function NayaxProductAutoMapDialog({ open, onOpenChange, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({}); // {nayax_product_id: bool}
  const [applying, setApplying] = useState(false);
  const [search, setSearch] = useState("");

  React.useEffect(() => {
    if (!open) {
      setData(null);
      setSelected({});
      setSearch("");
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .post("/nayax/auto-map-products")
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        const props = r.data.proposals || [];
        // Sélectionne par défaut les "suggested" avec confidence ≥ 0.75
        const initSel = {};
        for (const p of props) {
          if (p.status === "suggested" && p.confidence >= 0.75) {
            initSel[p.nayax_product_id] = true;
          }
        }
        setSelected(initSel);
        if (r.data.machines_errors?.length > 0) {
          toast.warning(
            `${r.data.machines_errors.length} machine(s) en erreur (voir liste rouge)`,
          );
        }
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Erreur récupération des propositions");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const proposals = data?.proposals || [];
  const filtered = useMemo(() => {
    if (!search.trim()) return proposals;
    const q = search.trim().toLowerCase();
    return proposals.filter(
      (p) =>
        (p.nayax_name || "").toLowerCase().includes(q) ||
        (p.product_name || "").toLowerCase().includes(q) ||
        (p.product_sku || "").toLowerCase().includes(q) ||
        String(p.nayax_product_id).includes(q),
    );
  }, [proposals, search]);

  const toggle = (npid) => {
    setSelected((prev) => ({ ...prev, [npid]: !prev[npid] }));
  };

  const selectAllSuggested = () => {
    const next = { ...selected };
    for (const p of filtered) {
      if (p.status === "suggested") next[p.nayax_product_id] = true;
    }
    setSelected(next);
  };
  const deselectAll = () => setSelected({});

  const selectableProposals = proposals.filter(
    (p) => (p.status === "suggested" || p.status === "conflict") && p.product_id,
  );
  const selectedCount = selectableProposals.filter(
    (p) => selected[p.nayax_product_id],
  ).length;

  const apply = async () => {
    const mappings = selectableProposals
      .filter((p) => selected[p.nayax_product_id])
      .map((p) => ({
        product_id: p.product_id,
        nayax_product_id: p.nayax_product_id,
      }));
    if (mappings.length === 0) {
      toast.info("Aucune proposition cochée à appliquer");
      return;
    }
    setApplying(true);
    try {
      const r = await api.post("/nayax/apply-product-mapping", { mappings });
      const { applied, unchanged, errors } = r.data;
      toast.success(
        `${applied} mapping(s) appliqué(s)${unchanged ? `, ${unchanged} inchangé(s)` : ""}`,
      );
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} erreur(s) — voir console`);
        console.warn("apply-product-mapping errors:", errors);
      }
      onOpenChange(false);
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'application");
    } finally {
      setApplying(false);
    }
  };

  const counts = data?.counts || { suggested: 0, already_mapped: 0, conflict: 0, unmatched: 0 };
  const machinesErrors = data?.machines_errors || [];

  const statusStyle = (s) => {
    if (s === "already_mapped") return "bg-[#E8F2EC] text-[#1F4E3D] border-[#A8C9B6]";
    if (s === "suggested") return "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]";
    if (s === "conflict") return "bg-[#FDF1E4] text-[#B36A24] border-[#E8C494]";
    return "bg-[#F9E4DF] text-[#A04E2C] border-[#E8B0A0]";
  };
  const statusLabel = (s) => {
    if (s === "already_mapped") return "Déjà OK";
    if (s === "suggested") return "Suggéré";
    if (s === "conflict") return "Conflit";
    return "Non trouvé";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="nayax-product-automap-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#1F4E3D]" />
            Auto-mapping produits Nayax
          </DialogTitle>
          <DialogDescription>
            Récupère les catalogues produit de tes machines Nayax et propose une correspondance avec tes
            produits AgroManage par similarité de nom.{" "}
            <strong>Coche les propositions à appliquer</strong>, puis clique « Appliquer » — chaque produit
            sera enregistré avec son <code className="font-mono text-[10px]">NayaxProductID</code>.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-[#575A57]">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Récupération des catalogues machines Nayax…
          </div>
        ) : !data ? (
          <div className="py-8 text-center text-[#575A57]">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Aucune donnée
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0 space-y-3">
            {/* Bandeau stats */}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge className="bg-[#F4F4F0] border border-[#E5E5E0] text-[#575A57]">
                {data.machines_ok}/{data.machines_scanned} machines lues
              </Badge>
              <Badge className={statusStyle("suggested")}>
                {counts.suggested} suggérés
              </Badge>
              <Badge className={statusStyle("already_mapped")}>
                {counts.already_mapped} déjà OK
              </Badge>
              <Badge className={statusStyle("conflict")}>
                {counts.conflict} conflits
              </Badge>
              <Badge className={statusStyle("unmatched")}>
                {counts.unmatched} sans match
              </Badge>
              <span className="ml-auto text-[#575A57]">
                <strong>{selectedCount}</strong> à appliquer
              </span>
            </div>

            {/* Erreurs machines si présentes */}
            {machinesErrors.length > 0 && (
              <div
                className="text-[11px] bg-[#FBEEEA] border border-[#E8B0A0] rounded p-2 text-[#A04E2C]"
                data-testid="nayax-product-automap-machines-errors"
              >
                <div className="flex items-center gap-1 font-semibold mb-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {machinesErrors.length} machine(s) inaccessible(s) :
                </div>
                <ul className="list-disc pl-4 space-y-0.5">
                  {machinesErrors.slice(0, 5).map((e) => (
                    <li key={e.distributor_id}>
                      <strong>{e.distributor_name}</strong> ({e.nayax_machine_id})
                      {e.http_status && ` — HTTP ${e.http_status}`}
                      {e.error && ` — ${String(e.error).slice(0, 100)}`}
                    </li>
                  ))}
                  {machinesErrors.length > 5 && <li>… +{machinesErrors.length - 5} autres</li>}
                </ul>
              </div>
            )}

            {/* Contrôles */}
            <div className="flex items-center gap-2">
              <Input
                type="text"
                placeholder="🔍 Recherche par nom Nayax, nom AgroManage, SKU ou ID…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 text-[13px]"
                data-testid="nayax-product-automap-search"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={selectAllSuggested}
                data-testid="nayax-product-automap-select-all-btn"
              >
                Cocher suggérés
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={deselectAll}
                data-testid="nayax-product-automap-deselect-btn"
              >
                Tout décocher
              </Button>
            </div>

            {/* Table des propositions */}
            <div
              className="flex-1 border border-[#E5E5E0] rounded overflow-y-auto"
              data-testid="nayax-product-automap-table"
            >
              {filtered.length === 0 ? (
                <div className="py-8 text-center text-[#575A57] text-sm">Aucune proposition</div>
              ) : (
                filtered.map((p) => {
                  const canSelect = (p.status === "suggested" || p.status === "conflict") && p.product_id;
                  return (
                    <div
                      key={p.nayax_product_id}
                      className="flex items-start gap-3 px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 text-[12px] hover:bg-[#F9F9F6]"
                      data-testid={`nayax-product-automap-row-${p.nayax_product_id}`}
                    >
                      <div className="flex-shrink-0 w-6 pt-0.5">
                        {canSelect && (
                          <input
                            type="checkbox"
                            checked={!!selected[p.nayax_product_id]}
                            onChange={() => toggle(p.nayax_product_id)}
                            className="cursor-pointer w-4 h-4"
                            data-testid={`product-automap-checkbox-${p.nayax_product_id}`}
                          />
                        )}
                        {p.status === "already_mapped" && (
                          <CheckCircle2 className="w-4 h-4 text-[#1F4E3D]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 grid grid-cols-2 gap-3">
                        {/* Côté Nayax */}
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase text-[#575A57] tracking-wider">
                            Nayax
                          </div>
                          <div className="font-mono text-[#1A1C1A] truncate">
                            #{p.nayax_product_id}
                          </div>
                          <div className="text-[#575A57] truncate">
                            {p.nayax_name || "(sans nom)"}
                          </div>
                          <div className="text-[10px] text-[#849F90] mt-0.5">
                            sur {p.machines_count} machine(s) :{" "}
                            {p.machines.map((m) => m.distributor_name).join(", ")}
                          </div>
                        </div>
                        {/* Côté AgroManage */}
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase text-[#575A57] tracking-wider">
                            AgroManage
                          </div>
                          {p.product_id ? (
                            <>
                              <div className="font-mono text-[#1A1C1A] truncate">
                                {p.product_sku || "(no sku)"}
                              </div>
                              <div className="text-[#575A57] truncate">
                                {p.product_name}
                              </div>
                              <div className="text-[10px] text-[#849F90] mt-0.5 flex items-center gap-2 flex-wrap">
                                <span>
                                  Stratégie : <strong>{p.strategy}</strong>
                                </span>
                                <span>
                                  Confiance : <strong>{Math.round(p.confidence * 100)}%</strong>
                                </span>
                                {p.status === "conflict" && (
                                  <span className="text-[#A04E2C]">
                                    ⚠ actuellement mappé à #{p.current_nayax_product_id}
                                  </span>
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="text-[#A04E2C] italic">
                              Aucun produit AgroManage correspondant. Crée-le manuellement dans /produits puis
                              relance.
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <Badge className={`text-[10px] ${statusStyle(p.status)} border`}>
                          {statusLabel(p.status)}
                        </Badge>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        <DialogFooter className="flex-shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="product-automap-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={apply}
            disabled={applying || selectedCount === 0}
            className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
            data-testid="product-automap-apply-btn"
          >
            {applying && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Appliquer ({selectedCount})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
